import { H4 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { TOrderSummary } from '@r1-frontend/ui-react/organisms/OrderSummary';
import ItemWithoutCost from '@r1-frontend/ui-react/organisms/OrderSummary/Item/ItemWithoutCost';
import { Indentation2SatisfyTheDesignIdea } from '@r1-frontend/ui-react/organisms/OrderSummary/styled';
import { TOrderSummaryItem } from '@r1-frontend/ui-react/organisms/OrderSummary/types';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { monthPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import ConnectionConditions from '~/src/components/ConnectionConditions/ConnectionConditions';
import TariffDetailedInfo from '~/src/features/full-buy/components/OrderSummary/TariffDetailedInfo';
import { getProductsCosts } from '~/src/features/full-buy/functions';
import { isConnectionCostHidden } from '~/src/features/full-buy/helpers/ab/isConnectionCostHidden';
import getAddressCheckingResult from '~/src/features/full-buy/store/selectors/getAddressCheckingResult';
import { selectCurrentTariff } from '~/src/features/full-buy/store/selectors/selectCurrentTariff';
import selectStepName from '~/src/features/full-buy/store/selectors/selectStepName';
import { TProduct } from '~/src/features/full-buy/store/transformShopItem';
import { TState } from '~/src/store';

import getDescriptionForTariffForNewSubscriptions from './getDescriptionForTariffForNewSubscriptions';
import processProduct from './processProduct';

export const monthText = getPluralize(monthPluralize);

const getSummaryInfo = (isVillage?: boolean, analyticsCallback?: () => void) => (state: TState): {
    fullSummaryData: TOrderSummary,
    total: TOrderSummaryItem,
} => {
    const selectedTariff = selectCurrentTariff(state);
    const stepName = selectStepName(state);
    const isShownLoader = state.fullBuy.isShownLoader;
    const addressCheckingResult = getAddressCheckingResult(state);

    const products = state.fullBuy.products;
    const total = {
        cost: 0,
        title: 'Итого при подключении:',
        Component: H4,
        props: {
            $withIndent: false,
            $inline: true,
        },
    };
    const orderSummary: TOrderSummary = {
        items: [],
    };

    if (!selectedTariff) {
        return {
            fullSummaryData: orderSummary,
            total,
        };
    } else {
        orderSummary.items = [];
    }

    const { additionalProducts, connectionPrice } = selectedTariff;

    const tariffPrice = selectedTariff.promoPrice || selectedTariff.price;
    const productsCost = getProductsCosts(products);

    const { instalmentProducts, oneTimePurchaseProducts } = products
        .reduce<{ instalmentProducts: TProduct[], oneTimePurchaseProducts: TProduct[] }>(
            (acc, product) => {
                if (product.selectedPrice?.duration) {
                    acc.instalmentProducts.push(product);
                }

                if (!product.selectedPrice?.duration) {
                    acc.oneTimePurchaseProducts.push(product);
                }

                return acc;
            }, { instalmentProducts: [], oneTimePurchaseProducts: [] },
        );

    const totalCost = {
        monthly: tariffPrice + productsCost.monthly,
        once: productsCost.once,
    };

    const allData = [];
    allData.push({
        title: `Тариф «${selectedTariff.title}»:`,
        description: getDescriptionForTariffForNewSubscriptions(state),
        cost: tariffPrice,
        Component: Paragraph4,
        color: COLORS.TextPrimary,
    });

    if (selectedTariff.promoPeriod) {
        allData.push({
            Component: TariffDetailedInfo,
            props: {
                period: selectedTariff.promoPeriod,
                startPrice: tariffPrice,
                finalPrice: selectedTariff.price,
            },
        });
    }
    allData.push({
        Component: Indentation2SatisfyTheDesignIdea,
        props: {},
    });

    additionalProducts.forEach(processProduct(allData));
    instalmentProducts.forEach(processProduct(allData, true));
    oneTimePurchaseProducts.forEach(processProduct(allData, true));

    if (!isConnectionCostHidden(stepName, !selectedTariff.isCottage && !isShownLoader)) {

        if (isVillage && !addressCheckingResult.newPrice) {
            allData.push({
                Component: ItemWithoutCost,
                props: {
                    title: 'Подключение',
                    color: COLORS.TextPrimary,
                },
            });
        } else {
            allData.push({
                title: 'Подключение',
                cost: connectionPrice.final,
                previousCost: connectionPrice.isDiscounted ? connectionPrice.base : undefined,
                color: COLORS.TextPrimary,
            });

            allData.push({
                Component: ConnectionConditions,
                props: {
                    isVillage,
                    analyticsCallback: analyticsCallback,
                },
            });

            totalCost.once += connectionPrice.final;
        }
    }

    total.cost = totalCost.monthly + totalCost.once;
    orderSummary.items.push(allData, 'separator', { total });

    return {
        fullSummaryData: orderSummary,
        total,
    };
};

export default getSummaryInfo;
